import { useSearchTableScope } from '@aibees/elephant/context/search-table-scope';
import { useSearchHistoryStore } from '@aibees/elephant/store/search-history-store';
import type { SearchTableScope } from '@aibees/elephant/store/search-history-store';
import { isEmpty } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import { useSearchQueryFilter } from './use-search-query-filter';

export interface UrlHistoryReturn {
  undo: VoidFunction;
  redo: VoidFunction;
  reset: VoidFunction;
  current: number;
  history: Record<string, string>[];
}

const EMPTY_SCOPE_SLICE = {
  history: [{}] as Record<string, string>[],
  current: 0,
};

const EMPTY_HISTORY: UrlHistoryReturn = {
  undo: () => {},
  redo: () => {},
  reset: () => {},
  current: 0,
  history: [{}],
};

export function useUrlHistory(): UrlHistoryReturn {
  const scope = useSearchTableScope();
  const { setParams, removeAllParams } = useSearchQueryFilter();

  const scopeHistorySelector = useCallback(
    (state: {
      historyByScope: Record<
        SearchTableScope,
        { history: Record<string, string>[]; current: number }
      >;
    }) => {
      if (scope !== 'company' && scope !== 'people') return EMPTY_SCOPE_SLICE;
      return state.historyByScope[scope];
    },
    [scope]
  );
  const scopeSlice = useSearchHistoryStore(scopeHistorySelector, shallow);
  const undoStore = useSearchHistoryStore.useUndo();
  const redoStore = useSearchHistoryStore.useRedo();
  const resetStore = useSearchHistoryStore.useReset();

  const hasScope = scope === 'company' || scope === 'people';
  const scopedScope = hasScope ? (scope as SearchTableScope) : null;
  const { history, current } = scopeSlice;

  const undo = useCallback(() => {
    if (!hasScope || !scopedScope) return;
    if (!current) return;
    const p = history[current - 1];
    if (isEmpty(p)) removeAllParams();
    else setParams(p, true, true);
    undoStore(scopedScope);
  }, [
    hasScope,
    scopedScope,
    current,
    history,
    removeAllParams,
    setParams,
    undoStore,
  ]);

  const redo = useCallback(() => {
    if (!hasScope || !scopedScope) return;
    const p = history[current + 1];
    if (p !== undefined) {
      setParams(p, true, true);
      redoStore(scopedScope);
    }
  }, [hasScope, scopedScope, current, history, setParams, redoStore]);

  const reset = useCallback(() => {
    if (!hasScope || !scopedScope) return;
    resetStore(scopedScope);
  }, [hasScope, scopedScope, resetStore]);

  const result = useMemo(
    () => ({
      undo,
      redo,
      reset,
      current,
      history,
    }),
    [undo, redo, reset, current, history]
  );

  return hasScope ? result : EMPTY_HISTORY;
}
